/**
 * ErrorBoundary - Capture et log toutes les erreurs React
 * Envoie automatiquement les erreurs au backend pour le debugging
 */
import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Bug, Download } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = '';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      reported: false 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Envoyer l'erreur au backend
    this.reportError(error, errorInfo);
  }

  async reportError(error, errorInfo) {
    try {
      const errorData = {
        error_message: error?.message || String(error),
        error_stack: error?.stack || '',
        component_name: errorInfo?.componentStack?.split('\n')[1]?.trim() || 'Unknown',
        url: window.location.href,
        user_action: this.getLastUserAction(),
        timestamp: new Date().toISOString(),
        browser_info: navigator.userAgent,
        extra_data: {
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          localStorage_keys: Object.keys(localStorage),
          sessionStorage_keys: Object.keys(sessionStorage),
          react_component_stack: errorInfo?.componentStack || ''
        }
      };

      await fetch(`${API_URL}/api/debug/log-frontend-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData)
      });

      this.setState({ reported: true });
      console.log('🔍 Erreur reportée au serveur pour debugging');
    } catch (reportError) {
      console.error('Impossible de reporter l\'erreur:', reportError);
    }
  }

  getLastUserAction() {
    // Récupérer la dernière action de l'utilisateur depuis le localStorage
    return localStorage.getItem('yas_last_action') || 'Unknown';
  }

  handleReload = () => {
    window.location.reload();
  };

  handleDownloadLogs = () => {
    window.open(`${API_URL}/api/debug/download-logs`, '_blank');
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-red-500/50 rounded-lg p-8 max-w-lg w-full text-center">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="h-16 w-16 text-red-500" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">
              Oups ! Une erreur s'est produite
            </h1>
            
            <p className="text-zinc-400 mb-6">
              L'application a rencontré un problème inattendu.
              {this.state.reported && (
                <span className="text-green-400 block mt-2">
                  ✓ L'erreur a été enregistrée pour analyse
                </span>
              )}
            </p>

            <div className="bg-zinc-800 rounded-lg p-4 mb-6 text-left">
              <p className="text-red-400 font-mono text-sm break-all">
                {this.state.error?.message || 'Erreur inconnue'}
              </p>
              {this.state.errorInfo?.componentStack && (
                <details className="mt-2">
                  <summary className="text-zinc-500 cursor-pointer text-sm">
                    Détails techniques
                  </summary>
                  <pre className="text-xs text-zinc-600 mt-2 overflow-auto max-h-32">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>

            <div className="flex gap-3 justify-center">
              <Button 
                onClick={this.handleReload}
                className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Recharger
              </Button>
              
              <Button 
                onClick={this.handleDownloadLogs}
                variant="outline"
                className="border-zinc-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Télécharger les logs
              </Button>
            </div>

            <p className="text-zinc-600 text-xs mt-6">
              <Bug className="h-3 w-3 inline mr-1" />
              Téléchargez les logs et envoyez-les pour diagnostic
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;


/**
 * Hook pour enregistrer les actions utilisateur (aide au debugging)
 */
export const useDebugAction = () => {
  const logAction = (action) => {
    try {
      localStorage.setItem('yas_last_action', action);
      localStorage.setItem('yas_last_action_time', new Date().toISOString());
    } catch (e) {
      // Ignore storage errors
    }
  };

  return logAction;
};


/**
 * Fonction utilitaire pour log manuellement une erreur sans crash
 */
export const logDebugError = async (message, context = {}) => {
  try {
    await fetch(`${API_URL}/api/debug/log-frontend-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        error_message: message,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        browser_info: navigator.userAgent,
        extra_data: context
      })
    });
    console.log('🔍 Debug log envoyé:', message);
  } catch (e) {
    console.error('Erreur lors du log debug:', e);
  }
};


/**
 * Fonction pour télécharger les logs directement
 */
export const downloadDebugLogs = () => {
  window.open(`${API_URL}/api/debug/download-logs`, '_blank');
};
